import {
  SpaceBetween,
  Button,
  FormField,
  Input,
  Form,
  Alert,
  KeyValuePairs,
  StatusIndicator,
  Header,
  ContentLayout,
  Container,
  Box,
  DatePicker,
  ColumnLayout,
  Toggle,
} from '@cloudscape-design/components';
import {
  CreateUserRequest,
  DeleteUserRequest,
  UpdateUserRequest,
  User,
} from 'src/types/orbit/User';
import { usePermission } from 'src/store/permission/usePermission';
import { memo, useEffect, useState } from 'react';
import RoleListDropdown from '../roles/RoleListDropdown';
import { useRoles } from 'src/store/role/useRoles';
import { Role } from 'src/types/orbit/Roles';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { useUsers } from 'src/store/users/useUsers';
import MultiSelectDivision from '../division/MultiSelectDivision';
import { useDivision } from 'src/store/division/useDivision';
import {
  checkFeaturePermission,
  convertList,
  findBusinessUnitById,
  findRolesById,
  formatDateTime,
  formatToISOWithMidnight,
} from 'src/utils/generalUtils';
import { Division } from 'src/types/orbit/Division';
import BusinessUnitListDropdown from '../businessUnit/BusinessUnitListDropdown';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import useUserProfile from 'src/hooks/useUserProfile';
import { Persona } from 'src/contstant/Persona';
import { useFormValidation } from 'src/hooks/useFormValidation';
import {
  UserFormValidator,
  UserFormValidationPaths,
} from 'src/validations/UserFormValidator';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import PermissionList from '../permission/PermissionList';
import { get, isEmpty } from 'lodash';
import RequiredFieldLabel from '../RequiredFieldLabel';

interface UserDetailsFormProps {
  user?: User | null;
  onViewdissmiss: () => void;
}

interface UserParams {
  alias: string;
  fullName: string;
  role: Role;
  permissions?: string[];
  divisions: Array<string>;
  permissionExpirationDate?: string;
  isActive: boolean;
  businessUnit: string;
}

export const closeUserForm = () => {
  document.dispatchEvent(new CustomEvent('closeUserForm', {}));
};
const UserDetailsForm = memo(
  ({ user, onViewdissmiss }: UserDetailsFormProps) => {
    const { closeView } = useSplitPanel();
    const { loadPermission, fetchPermissionsApiStatus, permissionsList } =
      usePermission();
    const { loadRoles, roles, fetchRolesApiStatus } = useRoles();
    const { getUserId, getPersona, getUserBusinessUnitId, getUserProgramId } =
      useUserProfile();
    const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
      useDivision();
    const { businessUnitList, loadBusinessUnits } = useBusinessUnit();

    const { createUser, updateUser, updateUserApiStatus, userPermission } = useUsers();

    const initValues: UserParams = {
      alias: user?.userPermission.userId || '',
      fullName: user?.userPermission.userName || '',
      role: findRolesById(roles, user?.userPermission?.roleId),
      permissions: user?.userPermission.overriddenPermission,
      divisions: user?.userPermission?.divisions || [],
      permissionExpirationDate: user?.userPermission?.permissionExpirationDate
        ? formatDateTime(user?.userPermission?.permissionExpirationDate, 'YYYY-MM-DD')
        : undefined,
      isActive: user?.userPermission.isUserActive ?? true,
      businessUnit: user?.userPermission.businessUnit
        ? user?.userPermission.businessUnit
        : getUserBusinessUnitId(),
    };

    const [userParams, setUserParams] = useState<UserParams>(initValues);
    const { fieldValidationError, validate } = useFormValidation(
      userParams,
      UserFormValidator(),
    );
    const isEditUser = checkFeaturePermission(
      getPersona(),
      permissionConfig.USERS.EDIT_USER,
      userPermission?.permissions,
    );

    const handleCloseUserForm = () => {
      closeView();
      onViewdissmiss();
    };
    //Load initial data
    useEffect(() => {
      loadBusinessUnits({
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
      loadRoles({
        businessUnitId: getUserBusinessUnitId()!,
        programId: getUserProgramId(),
        isRoleActive: true,
        persona: getPersona(),
      });
      loadPermission({ programId: getUserProgramId(), isActive: true });
      document.addEventListener('closeUserForm', handleCloseUserForm);
      return () => {
        document.removeEventListener('closeUserForm', handleCloseUserForm);
      };
    }, []);

    useEffect(() => {
      if (userParams.businessUnit) {
        loadDivisionsByBusinessUnit({
          businessUnitId: userParams!.businessUnit,
          programId: getUserProgramId(),
          isDeleted: false,
          isActive: true,
        });
      }
    }, [userParams.businessUnit]);

    useEffect(() => {
      setUserParams(initValues);
      if (!user) {
        onViewdissmiss();
      }
    }, [user, permissionsList, updateUserApiStatus.isSuccess]);

    useEffect(() => {
      setUserParams((prev) => {
        return {
          ...prev,
          divisions: user?.userPermission?.divisions || [],
        };
      });
    }, [divisionList]);

    const renderActionButton = () => {
      const onCancelClick = () => {
        closeView();
        onViewdissmiss();
      };
      const onSaveClick = () => {
        if (validate()) {
          const expiryDate = userParams!.permissionExpirationDate
            ? formatToISOWithMidnight(userParams!.permissionExpirationDate)
            : undefined;
          //if user is not null then consider to update user info else will create new user.
          if (user) {
            const updateUserData: UpdateUserRequest = {
              userId: userParams!.alias,
              programId: getUserProgramId(),
              roleId: userParams!.role!.roleId,
              overriddenPermission: userParams!.permissions,
              isUserActive: userParams!.isActive,
              userName: userParams!.fullName,
              updatedBy: getUserId(),
              businessUnit: userParams!.businessUnit,
              permissionExpirationDate: expiryDate,
              divisions: userParams.divisions,
            };

            updateUser(updateUserData);
          } else {
            const createUserData: CreateUserRequest = {
              userId: userParams!.alias,
              programId: getUserProgramId(),
              userName: userParams!.fullName,
              roleId: userParams!.role.roleId!,
              overriddenPermission: userParams!.permissions,
              isActive: userParams!.isActive,
              createdBy: getUserId(),
              businessUnit: userParams!.businessUnit,
              permissionExpirationDate: expiryDate,
              divisions: userParams.divisions,
            };

            createUser(createUserData);
          }
        }
      };

      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            data-testid="cancel-button"
            formAction="none"
            variant="link"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid="save-button"
            variant="primary"
            onClick={onSaveClick}
            loading={updateUserApiStatus.isLoading}
          >
            Save
          </Button>
        </SpaceBetween>
      );
    };

    const isUserIdReadOnly = () => {
      if (!isEditUser) {
        return true;
      }
      if (user) {
        return true;
      }
      return false;
    };

    const renderForm = () => {
      return (
        <form onSubmit={(e) => e.preventDefault()}>
          <Form actions={isEditUser && renderActionButton()}>
            <SpaceBetween direction="vertical" size="l">
              <FormField
                data-testid="alias-field"
                label={<RequiredFieldLabel label="Alias" />}
                errorText={fieldValidationError.alias}
              >
                <Input
                  readOnly={isUserIdReadOnly()}
                  data-testid="alias-input"
                  value={userParams!.alias}
                  onChange={({ detail }) =>
                    setUserParams((prevState) => ({
                      ...prevState,
                      alias: detail.value,
                    }))
                  }
                />
              </FormField>

              <FormField
                data-testid="full-name-field"
                label={<RequiredFieldLabel label="Full Name" />}
                errorText={fieldValidationError.fullName}
              >
                <Input
                  readOnly={!isEditUser}
                  data-testid="full-name-input"
                  value={userParams.fullName}
                  onChange={({ detail }) =>
                    setUserParams((prevState) => ({
                      ...prevState,
                      fullName: detail.value,
                    }))
                  }
                />
              </FormField>

              <FormField
                data-testid="busniess-unti-field"
                label={<RequiredFieldLabel label="Business Unit" />}
              >
                <Input
                  data-testid="businessUnit-input"
                  value={
                    businessUnitList.find(
                      (bu) => bu.businessUnitId === userParams!.businessUnit,
                    )?.name || userParams!.businessUnit!
                  }
                  readOnly
                />
              </FormField>
              <FormField
                data-testid="divisions-field"
                label="Divisions"
                errorText={fieldValidationError.divisions}
              >
                <MultiSelectDivision
                  readOnly={!isEditUser}
                  divisions={divisionList}
                  isLoading={fetcDivisionsApiStatus.isLoading}
                  error={fetcDivisionsApiStatus.errorMessage}
                  selectedDivisionList={userParams.divisions}
                  onItemSelection={(items: Division[]) => {
                    setUserParams((prevState) => ({
                      ...prevState,
                      divisions: convertList<Division, string>(
                        items,
                        (item) => item.divisionName,
                      ),
                    }));
                  }}
                />
              </FormField>
              <FormField
                data-testid="role-field"
                label={<RequiredFieldLabel label="Role" />}
                errorText={fieldValidationError.role}
              >
                <RoleListDropdown
                  isLoading={fetchRolesApiStatus.isLoading}
                  roleList={roles.filter((item) => {
                    if (getPersona() !== Persona.PLATFORM_ADMIN) {
                      return item.roleId !== Persona.PLATFORM_ADMIN;
                    }
                    return true;
                  })}
                  readOnly={!isEditUser}
                  error={fetchRolesApiStatus.errorMessage}
                  selectedRole={userParams.role}
                  onRoleChange={(role: Role) => {
                    setUserParams((prevState) => ({
                      ...prevState,
                      role: role,
                      permissions: [],
                    }));
                  }}
                />
              </FormField>
              {userParams.role &&
                userParams.role.roleId !== Persona.PLATFORM_ADMIN &&
                userParams.role.roleId !== Persona.BUSINESS_UNIT_ADMIN && (
                  <PermissionList
                    isLoading={fetchPermissionsApiStatus.isLoading}
                    title="Additional Permissions"
                    permissions={permissionsList}
                    selectedOldPermissions={userParams.permissions}
                    excludePermissions={[
                      ...(userParams.role?.overriddenPermission || []),
                      ...(userParams?.role?.defaultPermissions || []),
                    ]}
                    footerEnabled={false}
                    readOnly={!isEditUser}
                    showSelectedPermissionsOnly={!isEditUser}
                    error={fetchPermissionsApiStatus.errorMessage}
                    onChangePermission={(list: string[]) => {
                      setUserParams((prevState) => ({
                        ...prevState,
                        permissions: list,
                      }));
                    }}
                  />
                )}

              <ColumnLayout columns={2}>
                <FormField
                  data-testid="permission-expiry-field"
                  label="Permission expiry"
                  constraintText="Use YYYY/MM/DD format."
                  errorText={fieldValidationError.permissionExpirationDate}
                >
                  <DatePicker
                    data-testid="permission-expiry-picker"
                    onChange={({ detail }) =>
                      setUserParams((prevState) => ({
                        ...prevState,
                        permissionExpirationDate: detail.value,
                      }))
                    }
                    value={userParams.permissionExpirationDate || ''}
                    openCalendarAriaLabel={(selectedDate) =>
                      'Choose permission expiry date' +
                      (selectedDate ? `, selected date is ${selectedDate}` : '')
                    }
                    placeholder="YYYY/MM/DD"
                  />
                </FormField>

                <FormField
                  label="Activation Status"
                  constraintText="Enable or disable the user account using this switch."
                >
                  <Toggle
                    readOnly={!isEditUser}
                    onChange={({ detail }) => {
                      setUserParams((prevState) => ({
                        ...prevState,
                        isActive: detail.checked,
                      }));
                    }}
                    checked={userParams.isActive}
                  >
                    {userParams.isActive ? 'Active' : 'Inactive'}
                  </Toggle>
                </FormField>
              </ColumnLayout>
            </SpaceBetween>
          </Form>

          {updateUserApiStatus.errorMessage && (
            <Box margin={{ top: 'xl' }}>
              <Alert type="error">{updateUserApiStatus.errorMessage}</Alert>
            </Box>
          )}
        </form>
      );
    };

    return <Box>{renderForm()}</Box>;
  },
  (prevProps, nextProps) => {
    // Return true if we should NOT re-render
    // Return false if we should re-render

    // Handle cases where both are null/undefined
    if (!prevProps.user && !nextProps.user) {
      return true;
    }

    // Handle cases where one is null/undefined
    if (!prevProps.user || !nextProps.user) {
      return false;
    }

    // Compare relevant user properties
    const prevUser = prevProps.user;
    const nextUser = nextProps.user;

    return (
      prevUser.userPermission.userId === nextUser.userPermission.userId &&
      prevProps.onViewdissmiss === nextProps.onViewdissmiss
    );
  },
);

export default UserDetailsForm;
